import { FC, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { burgerMenuStore } from '../model/store';
import { BurgerButton } from './BurgerButton';
import styled from 'styled-components';
import { colors } from 'shared/styles/colors';
import logoSvg from '../../../app/assets/logo.svg';
import RegisterSvg from '../assets/register.svg';
import { BurgerAuthButton } from './BurgerAuthButton';
import { TokenStorage } from '../../../shared/lib/token-storage';
import { userStore } from '../../../entities/user/model';
import { useNavigate, useLocation } from 'react-router-dom';
import { AvatarIcon } from 'shared/ui/Button/icons';

// Desktop Header Styles
const DesktopHeaderWrapper = styled.header`
  background-color: #142D6F;
  color: white;
  padding: 12px 45px;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  position: sticky;
  top: 0;
  z-index: 100;

  @media (max-width: 1024px) {
    display: none;
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-right: auto;
`;

const UkrainianFlag = styled.div`
  width: 15px;
  height: 62px;
  background: linear-gradient(to bottom, #005BBB 50%, #FFD700 50%);
`;

const SiteName = styled.span`
  font-weight: bold;
  font-size: 1.1em;
  cursor: pointer;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-left: auto;
`;

const MenuButton = styled.div`
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const MenuIcon = styled.div`
  width: 20px;
  height: 20px;
  position: relative;
  
  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 2px;
    background-color: white;
    transition: all 0.3s ease;
  }
  
  &::before {
    top: 6px;
  }
  
  &::after {
    top: 12px;
  }
  
  &:after {
    box-shadow: 0 -6px 0 white;
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  
  &:hover {
    opacity: 0.8;
  }
`;

const UserName = styled.span`
  font-size: 1em;
`;

const CentralLogoImage = styled.img`
  height: 64px;
  width: 51px;
  
`;

// Mobile Header Styles
const MobileHeaderWrapper = styled.div`
  background-color: ${colors.accentPrimary};
  height: 60px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  position: relative;
  z-index: 10;

  @media (min-width: 1025px) {
    display: none;
  }
`;

const MobileLogo = styled.img`
  height: 46px;
`;

const MobileUserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const MobileUserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  color: white;
  
  &:hover {
    opacity: 0.8;
  }
`;

// Navigation Menu Styles
const StyledNav = styled.nav<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-color: ${colors.accentPrimary};
  padding-top: 80px;
  transition: transform 0.3s ease, opacity 0.3s ease, visibility 0.3s ease;
  transform: ${({isOpen}) => (isOpen ? 'translateX(0)' : 'translateX(-100%)')};
  opacity: ${({isOpen}) => (isOpen ? 1 : 0)};
  visibility: ${({isOpen}) => (isOpen ? 'visible' : 'hidden')};
  z-index: 999;
  overflow-y: auto;
  overscroll-behavior: contain;
  display: flex;
  flex-direction: column;
  
  @media (min-width: 1025px) {
    width: 400px;
    right: 0;
    left: auto;
    transform: ${({isOpen}) => (isOpen ? 'translateX(0)' : 'translateX(100%)')};
    box-shadow: -4px 0 20px rgba(0, 0, 0, 0.3);
    border-radius:50px;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  li {
    margin: 0 auto;
    padding: 0 30px;

    & > a, & > button {
      text-align: center;
      color: ${colors.accentWhite};
      text-decoration: none;
      font-size: 24px;
      display: block;
      padding: 16px 8px;
      background: none;
      border: none;
      cursor: pointer;
      width: 100%;

      &:hover {
        color: ${colors.accentQuaternary6};
      }
    }
  }

  & > img {
    height: 76px;
    width: 96px;
    margin: 0 auto;
    margin-bottom: 86px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  font-size: 20px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const Overlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 998;
  opacity: ${({isOpen}) => (isOpen ? 1 : 0)};
  visibility: ${({isOpen}) => (isOpen ? 'visible' : 'hidden')};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  
  @media (max-width: 1024px) {
    display: none;
  }
`;

export const BurgerMenu: FC = observer(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = TokenStorage.hasTokens();
  const userName = userStore.user?.first_name;

  const isLandingPage = location.pathname === '/';

  useEffect(() => {
    if (burgerMenuStore.isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [burgerMenuStore.isOpen]);

  const handleNavigation = (path: string) => {
    burgerMenuStore.close();
    navigate(path);
  };

  const handleLogout = () => {
    userStore.logout();
    burgerMenuStore.close();
    navigate('/');
  };

  return (
    <>
      {/* Desktop Header - Conditionally rendered */}
      {!isLandingPage && (
        <DesktopHeaderWrapper>
          <LeftSection>
            <UkrainianFlag />
            <div className='cursor-pointer' onClick={() => navigate('/')}>
            <SiteName>ВККСУ Тестування IQ</SiteName>
              <div className='text-sm '>Комерційний сайт України</div>
            </div>
          </LeftSection>

          <CentralLogoImage src={logoSvg} alt="Company Logo" />

          <RightSection>
            <UserInfo onClick={() => navigate('/purchases')}>
              Мої тести
            </UserInfo>
            {isAuthenticated && (
              <UserInfo onClick={() => navigate('/profile')}>
                <UserName>{userName}</UserName>
                <AvatarIcon/>
              </UserInfo>
            )}
            <MenuButton onClick={() => burgerMenuStore.toggle()}>
              <MenuIcon />
            </MenuButton>
          </RightSection>
        </DesktopHeaderWrapper>
      )}

      {/* Mobile Header - Conditionally rendered */}
      {!isLandingPage && (
        <MobileHeaderWrapper>
          <div className="w-8"/>
          {!burgerMenuStore.isOpen && <MobileLogo src={logoSvg} alt="logo" height={46}/>}
          
          <MobileUserSection>
            {isAuthenticated && (
              <MobileUserInfo onClick={() => navigate('/profile')}>
                <UserName>{userName}</UserName>
                <AvatarIcon/>
              </MobileUserInfo>
            )}
            <BurgerButton/>
          </MobileUserSection>
        </MobileHeaderWrapper>
      )}

      {/* Desktop Overlay */}
      <Overlay isOpen={burgerMenuStore.isOpen} onClick={() => burgerMenuStore.close()} />

      {/* Mobile Navigation Menu */}
      <StyledNav
        aria-hidden={!burgerMenuStore.isOpen}
        isOpen={burgerMenuStore.isOpen}
      >
        <CloseButton onClick={() => burgerMenuStore.close()}>
          ×
        </CloseButton>
        <img src={logoSvg} alt="logo"/>
        <ul>
          <li><button onClick={() => handleNavigation('/')}>Головна сторінка</button></li>
          {isAuthenticated ? (
            <>
              <li><button onClick={() => handleNavigation('/profile')}>Персональний кабінет</button></li>
              <li><button onClick={() => handleNavigation('/notifications')}>Сповіщення</button></li>
              <li><button onClick={() => handleNavigation('/purchases')}>Мої покупки</button></li>
              <li><button onClick={() => handleNavigation('/achievements')}>Досягнення</button></li>
              <li><button onClick={() => handleNavigation('/faq')}>FAQ</button></li>
            </>
          ) : null}
        </ul>
        <div className="flex flex-1 flex-col justify-end">
          <div className='flex gap-1'>
            {isAuthenticated ? (
              <button 
                className='flex-1 flex-row justify-center items-center gap-8 h-[56px] text-[#FFAAAA]' 
                onClick={handleLogout}
              >
                Вийти з акаунту
              </button>
            ) : (
              <>
                <BurgerAuthButton 
                  icon={logoSvg} 
                  text="Увійти" 
                  to={'/login'} 
                  onClick={burgerMenuStore.close}
                />
                <BurgerAuthButton 
                  icon={RegisterSvg} 
                  text="Реєстрація" 
                  to={'/register'} 
                  onClick={burgerMenuStore.close}
                />
              </>
            )}
          </div>
        </div>
      </StyledNav>
    </>
  );
});